import * as CANNON from 'cannon-es';
import * as THREE from 'three';
import { PHYSICS_TUNING, WORLD_SCALE } from '../../shared/gameConfig';

// Physics layers for filtering collisions
export enum PhysicsLayer {
  PLAYER = 1,
  STATIC = 2,
  DYNAMIC = 4,
  TRIGGER = 8,
  PROJECTILE = 16
}

// Best practice: Define physics constants
const PHYSICS_CONFIG = {
  GRAVITY: -20,
  FIXED_TIME_STEP: 1 / 60,
  MAX_SUB_STEPS: 3,
  PLAYER_MASS: 5,
  PLAYER_LINEAR_DAMPING: 0.9,
  PLAYER_ANGULAR_DAMPING: 0.999,
  DEFAULT_FRICTION: 0.4,
  DEFAULT_RESTITUTION: 0.1
};

export class PhysicsManager {
  private world: CANNON.World;
  private bodies: Map<string, CANNON.Body> = new Map();
  private debugMeshes: THREE.Mesh[] = [];
  private playerBody: CANNON.Body | null = null;
  private obstacleBodies: CANNON.Body[] = [];
  private groundBodies: CANNON.Body[] = [];
  private playerOnGround = false;
  
  // Collision callbacks
  private collisionCallbacks: Map<string, (event: any) => void> = new Map();
  
  // Debug mode
  private debugMode = false;
  
  // Materials for different surface interactions
  private materials = {
    player: new CANNON.Material('player'),
    ground: new CANNON.Material('ground'),
    obstacle: new CANNON.Material('obstacle')
  };

  constructor() {
    // Initialize physics world first
    this.world = new CANNON.World({
      gravity: new CANNON.Vec3(0, PHYSICS_CONFIG.GRAVITY, 0),
      solver: new CANNON.GSSolver()
    });
    
    // Then set the broadphase
    this.world.broadphase = new CANNON.SAPBroadphase(this.world);
    
    // Optimize solver iterations for performance
    (this.world.solver as CANNON.GSSolver).iterations = 10;
    (this.world.solver as CANNON.GSSolver).tolerance = 0.1;
    
    // Set up world parameters
    this.world.defaultContactMaterial.friction = PHYSICS_CONFIG.DEFAULT_FRICTION;
    this.world.defaultContactMaterial.restitution = PHYSICS_CONFIG.DEFAULT_RESTITUTION;
    
    // Enable collision event reporting
    this.world.addEventListener('beginContact', this.handleBeginContact.bind(this));
    this.world.addEventListener('endContact', this.handleEndContact.bind(this));
    
    // Create contact materials for different surface interactions
    this.setupContactMaterials();
  }
  
  private setupContactMaterials() {
    // Player-Ground interaction
    const playerGroundContact = new CANNON.ContactMaterial(
      this.materials.player,
      this.materials.ground,
      {
        friction: 0.6,
        restitution: 0.05,
        contactEquationStiffness: 1e8,
        contactEquationRelaxation: 3
      }
    );
    
    // Player-Obstacle interaction
    const playerObstacleContact = new CANNON.ContactMaterial(
      this.materials.player,
      this.materials.obstacle,
      {
        friction: 0.0, // Slippery walls for better movement
        restitution: 0.0,
        contactEquationStiffness: 1e8,
        contactEquationRelaxation: 3
      }
    );
    
    this.world.addContactMaterial(playerGroundContact);
    this.world.addContactMaterial(playerObstacleContact);
  }
  
  private handleBeginContact(event: any) {
    // Check if player is touching ground
    if (this.playerBody && (event.bodyA === this.playerBody || event.bodyB === this.playerBody)) {
      // const otherBody = event.bodyA === this.playerBody ? event.bodyB : event.bodyA;
      
      // Check contact normal to determine if on ground
      const contactNormal = new CANNON.Vec3();
      event.contact.ni.copy(contactNormal);
      
      if (event.bodyA === this.playerBody) {
        contactNormal.negate(contactNormal);
      }
      
      // If normal points up, player is on ground
      if (contactNormal.y > 0.5) {
        this.playerOnGround = true;
      }
      
      // Trigger callbacks if registered
      const callback = this.collisionCallbacks.get('player');
      if (callback) {
        callback(event);
      }
    }
  }
  
  private handleEndContact(event: any) {
    // Check if player left ground
    if (this.playerBody && (event.bodyA === this.playerBody || event.bodyB === this.playerBody)) {
      // Simple check - may need more sophisticated ground detection
      this.playerOnGround = false;
    }
  }

  // Create player physics body
  createPlayerBody(position: THREE.Vector3): CANNON.Body {
    // Player capsule shape (better than box for smooth movement)
    const radius = WORLD_SCALE.PLAYER_WIDTH * 0.4;
    // const height = WORLD_SCALE.PLAYER_HEIGHT - radius * 2; // For future capsule implementation
    
    // Create compound shape for capsule
    const shape = new CANNON.Box(new CANNON.Vec3(
      radius,
      WORLD_SCALE.PLAYER_HEIGHT * 0.5,
      radius
    ));

    this.playerBody = new CANNON.Body({
      mass: PHYSICS_CONFIG.PLAYER_MASS,
      shape,
      material: this.materials.player,
      position: new CANNON.Vec3(position.x, position.y, position.z),
      fixedRotation: true, // Prevent player from tipping over
      linearDamping: PHYSICS_CONFIG.PLAYER_LINEAR_DAMPING,
      angularDamping: PHYSICS_CONFIG.PLAYER_ANGULAR_DAMPING,
      collisionFilterGroup: PhysicsLayer.PLAYER,
      collisionFilterMask: PhysicsLayer.STATIC | PhysicsLayer.DYNAMIC | PhysicsLayer.TRIGGER
    });
    
    // Store player body reference
    this.bodies.set('player', this.playerBody);
    this.world.addBody(this.playerBody);
    
    return this.playerBody;
  }

  // Create static terrain/ground body
  createTerrainBody(heightData: number[][], width: number, depth: number): CANNON.Body {
    const shape = new CANNON.Heightfield(heightData, {
      elementSize: 1
    });

    const groundBody = new CANNON.Body({
      mass: 0, // Static body
      shape,
      material: this.materials.ground
    });

    // Position the heightfield correctly
    const quaternion = new CANNON.Quaternion();
    quaternion.setFromAxisAngle(new CANNON.Vec3(1, 0, 0), -Math.PI / 2);
    groundBody.quaternion = quaternion;
    groundBody.position.set(-width / 2, 0, -depth / 2);

    this.world.addBody(groundBody);
    this.groundBodies.push(groundBody);
    return groundBody;
  }

  // Create obstacle/wall body with proper collision setup
  createObstacleBody(position: THREE.Vector3, size: THREE.Vector3, id?: string): CANNON.Body {
    const shape = new CANNON.Box(new CANNON.Vec3(
      size.x * 0.5,
      size.y * 0.5,
      size.z * 0.5
    ));

    const body = new CANNON.Body({
      mass: 0, // Static obstacle
      shape,
      material: this.materials.obstacle,
      position: new CANNON.Vec3(position.x, position.y, position.z),
      collisionFilterGroup: PhysicsLayer.STATIC,
      collisionFilterMask: PhysicsLayer.PLAYER | PhysicsLayer.DYNAMIC | PhysicsLayer.PROJECTILE
    });

    // Store reference
    if (id) {
      this.bodies.set(id, body);
    }
    this.obstacleBodies.push(body);
    this.world.addBody(body);
    
    return body;
  }
  
  // Clear all obstacles (useful when loading new map)
  clearObstacles() {
    for (const body of this.obstacleBodies) {
      this.world.removeBody(body);
    }
    this.obstacleBodies = [];
    
    // Clear from bodies map
    for (const [key] of this.bodies) {
      if (key.startsWith('obstacle_')) {
        this.bodies.delete(key);
      }
    }
  }

  // Apply movement forces to player
  movePlayer(direction: THREE.Vector3, jump: boolean = false) {
    if (!this.playerBody) return;

    // Direct velocity control for responsive movement
    const targetSpeed = PHYSICS_TUNING.PLAYER_MAX_SPEED;
    const acceleration = 50; // Much stronger acceleration
    
    // Set velocity directly for immediate response
    const targetVelX = direction.x * targetSpeed;
    const targetVelZ = direction.z * targetSpeed;
    
    // Smooth interpolation to target velocity
    const lerpFactor = 1.0 - Math.exp(-acceleration * (1/60));
    this.playerBody.velocity.x = this.playerBody.velocity.x * (1 - lerpFactor) + targetVelX * lerpFactor;
    this.playerBody.velocity.z = this.playerBody.velocity.z * (1 - lerpFactor) + targetVelZ * lerpFactor;

    // Handle jumping
    if (jump && this.isPlayerOnGround()) {
      this.playerBody.velocity.y = PHYSICS_TUNING.PLAYER_JUMP_FORCE;
    }
  }

  // Check if player is on ground
  isPlayerOnGround(): boolean {
    if (!this.playerBody) return false;
    
    // Use collision-based ground detection for better accuracy
    this.playerOnGround = false; // Reset
    
    // Ray cast down from player position as backup
    const from = this.playerBody.position.clone();
    const to = from.clone();
    to.y -= WORLD_SCALE.PLAYER_HEIGHT * 0.6;

    const result = new CANNON.RaycastResult();
    this.world.raycastClosest(from, to, {}, result);
    
    return this.playerOnGround || result.hasHit;
  }

  // Get player position
  getPlayerPosition(): THREE.Vector3 {
    if (!this.playerBody) return new THREE.Vector3();
    return new THREE.Vector3(
      this.playerBody.position.x,
      this.playerBody.position.y,
      this.playerBody.position.z
    );
  }

  // Set player position
  setPlayerPosition(position: THREE.Vector3) {
    if (!this.playerBody) return;
    this.playerBody.position.set(position.x, position.y, position.z);
    this.playerBody.velocity.set(0, 0, 0);
  }

  // Update physics simulation
  update(deltaTime: number) {
    this.world.step(PHYSICS_CONFIG.FIXED_TIME_STEP, deltaTime, PHYSICS_CONFIG.MAX_SUB_STEPS);
    
    // Update debug visualization if enabled
    if (this.debugMode) {
      this.updateDebugMeshes();
    }
  }

  // Sync Three.js meshes with physics bodies
  syncMeshWithBody(mesh: THREE.Mesh, body: CANNON.Body) {
    mesh.position.copy(body.position as any);
    mesh.quaternion.copy(body.quaternion as any);
  }

  // Create projectile body for bullets
  createProjectileBody(position: THREE.Vector3, direction: THREE.Vector3): CANNON.Body {
    const shape = new CANNON.Sphere(0.1);
    const body = new CANNON.Body({
      mass: 0.01,
      shape,
      position: new CANNON.Vec3(position.x, position.y, position.z),
      velocity: new CANNON.Vec3(
        direction.x * PHYSICS_TUNING.PROJECTILE_SPEED,
        direction.y * PHYSICS_TUNING.PROJECTILE_SPEED,
        direction.z * PHYSICS_TUNING.PROJECTILE_SPEED
      )
    });

    this.world.addBody(body);
    
    // Auto-remove after lifetime
    setTimeout(() => {
      this.world.removeBody(body);
    }, PHYSICS_TUNING.PROJECTILE_LIFETIME * 1000);

    return body;
  }

  // Remove body from world
  removeBody(body: CANNON.Body) {
    this.world.removeBody(body);
  }

  // Clean up
  dispose() {
    // Remove all bodies
    for (const body of this.world.bodies) {
      this.world.removeBody(body);
    }
    this.bodies.clear();
    this.debugMeshes = [];
  }

  // Debug visualization
  createDebugMesh(body: CANNON.Body): THREE.Mesh | null {
    let geometry: THREE.BufferGeometry | null = null;
    
    if (body.shapes[0] instanceof CANNON.Box) {
      const shape = body.shapes[0] as CANNON.Box;
      geometry = new THREE.BoxGeometry(
        shape.halfExtents.x * 2,
        shape.halfExtents.y * 2,
        shape.halfExtents.z * 2
      );
    } else if (body.shapes[0] instanceof CANNON.Sphere) {
      const shape = body.shapes[0] as CANNON.Sphere;
      geometry = new THREE.SphereGeometry(shape.radius);
    }

    if (geometry) {
      const material = new THREE.MeshBasicMaterial({
        color: 0x00ff00,
        wireframe: true,
        opacity: 0.3,
        transparent: true
      });
      const mesh = new THREE.Mesh(geometry, material);
      this.debugMeshes.push(mesh);
      return mesh;
    }

    return null;
  }

  updateDebugMeshes() {
    this.debugMeshes.forEach((mesh, index) => {
      if (this.world.bodies[index]) {
        mesh.position.copy(this.world.bodies[index].position as any);
        mesh.quaternion.copy(this.world.bodies[index].quaternion as any);
      }
    });
  }
  
  // Enable/disable debug mode
  setDebugMode(enabled: boolean) {
    this.debugMode = enabled;
  }
  
  // Perform a raycast in the physics world
  raycast(from: THREE.Vector3, to: THREE.Vector3): CANNON.RaycastResult | null {
    const result = new CANNON.RaycastResult();
    const fromVec = new CANNON.Vec3(from.x, from.y, from.z);
    const toVec = new CANNON.Vec3(to.x, to.y, to.z);
    
    const hit = this.world.raycastClosest(fromVec, toVec, {
      skipBackfaces: true,
      collisionFilterMask: PhysicsLayer.STATIC | PhysicsLayer.DYNAMIC
    }, result);
    
    return hit ? result : null;
  }
  
  // Get all bodies within a radius (useful for explosions, area effects)
  getBodiesInRadius(position: THREE.Vector3, radius: number): CANNON.Body[] {
    const bodies: CANNON.Body[] = [];
    const pos = new CANNON.Vec3(position.x, position.y, position.z);
    
    for (const body of this.world.bodies) {
      const distance = body.position.distanceTo(pos);
      if (distance <= radius) {
        bodies.push(body);
      }
    }
    
    return bodies;
  }
  
  // Register collision callback
  addCollisionCallback(id: string, callback: (event: any) => void) {
    this.collisionCallbacks.set(id, callback);
  }
  
  // Remove collision callback
  removeCollisionCallback(id: string) {
    this.collisionCallbacks.delete(id);
  }
}
